import { useRef, useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// Devuelve true cuando corre en navegador web (no Android ni iOS nativo)
const checkIsWeb = () => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return false;
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return false;
  return true;
};

const getHandlerSize = () => {
  const w = window.innerWidth;
  if (w < 340) return 56;
  if (w < 390) return 64;
  return 72;
};

export default function SliderAction({ actionButton, onComplete, reset }: any) {
  const HANDLER_SIZE = getHandlerSize();

  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [idle, setIdle] = useState(true);
  const [isWeb] = useState(() => checkIsWeb());

  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reset) {
      setOffset(0);
      setCompleted(false);
      setIdle(true);
    }
  }, [reset]);

  const vibrate = (ms = 50) => {
    if ("vibrate" in navigator && isIOS()) {
      navigator.vibrate(ms);
    } else if ("vibrate" in navigator) {
      navigator.vibrate(ms);
    }
  };

  const handleMouseDown = () => {
    if (completed) return;
    setDragging(true);
    setIdle(false);
  };

  const handleMouseUp = () => {
    if (!dragging) return;
    setDragging(false);

    const width = sliderRef.current?.offsetWidth || 0;

    if (offset >= width - HANDLER_SIZE) {
      setCompleted(true);
      vibrate(100);

      // ✅ Delay pequeño para asegurar que el slider renderice antes de abrir modales
      setTimeout(() => {
        onComplete(actionButton);
      }, 50);
    } else {
      setOffset(0);
      setIdle(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (!dragging) return;

    const width = sliderRef.current?.offsetWidth || 0;
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return;

    let clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    let newOffset = clientX - rect.left - HANDLER_SIZE / 2;

    if (newOffset < 0) newOffset = 0;
    if (newOffset > width - HANDLER_SIZE) newOffset = width - HANDLER_SIZE;

    setOffset(newOffset);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleMouseMove);
    window.addEventListener("touchend", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  });

  // ── En WEB: botón normal en lugar de slider ───────────────────────────────
  if (isWeb) {
    const colorClass =
      actionButton.color === "danger"
        ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
        : actionButton.color === "warning"
        ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
        : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700";

    return (
      <div className="mt-3 w-full">
        <button
          onClick={() => onComplete(actionButton)}
          className={`w-full h-12 xs:h-14 rounded-xl text-white font-bold text-sm xs:text-base tracking-wide transition-all active:scale-[0.97] shadow-md flex items-center justify-center gap-2 ${colorClass}`}
        >
          <ChevronRight className="w-4 h-4 xs:w-5 xs:h-5" />
          {actionButton.label}
        </button>
      </div>
    );
  }

  // ── En ANDROID / iOS: slider original ─────────────────────────────────────
  return (
    <div className="mt-6 w-full px-1">
      <p className="text-base xs:text-lg mb-2 text-center">
        Desliza para marcar {actionButton.label.toLowerCase()}
      </p>

      <div
        ref={sliderRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        className="relative rounded-full bg-muted/30 overflow-hidden select-none border shadow-inner"
        style={{ height: HANDLER_SIZE + "px" }}
      >
        <div
          className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-300 ease-out"
          style={{ width: offset + HANDLER_SIZE }}
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-white font-medium truncate pr-2" style={{ maxWidth: `calc(100% - ${HANDLER_SIZE + 8}px)` }}>
          {completed
            ? `${actionButton.label} marcada`
            : `Desliza para marcar ${actionButton.label.toLowerCase()}`}
        </span>

        <div
          className={`absolute top-0 left-0 rounded-full flex items-center justify-center shadow-xl border
          transition-all duration-300 ease-out
          ${completed ? "bg-green-500 text-white" : "bg-white"}
          ${idle && !dragging && !completed ? "animate-bounce-horizontal" : ""}
        `}
          style={{
            width: HANDLER_SIZE + "px",
            height: HANDLER_SIZE + "px",
            transform: `translateX(${offset}px)`,
          }}
        >
          <ChevronRight className="w-8 h-8 text-primary" />
        </div>
      </div>
    </div>
  );
}
